import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Template} from "../../../../model/template";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {Tenant} from "../../../../model/tenant";
import {DeviceCategory} from "../../../../model/device-category";
import {TenantsService} from "../../../../services/tenants.service";
import {AuthGuard} from "../../../../common/auth-guard.service";
import {PreviousRouteService} from "../../../../services/previous-route.service";
import {noSpecialCharacters} from "../../../../shared/custom-validators";
import {TranslocoService} from "@ngneat/transloco";
import {SelectItem} from "primeng/api";

@Component({
  selector: 'app-templates-form-info',
  templateUrl: './templates-form-info.component.html',
  styleUrls: ['./templates-form-info.component.scss']
})
export class TemplatesFormInfoComponent implements OnInit {

  @Input() mode: 'create' | 'edit';

  @Input() template: Template;
  @Output() templateChange = new EventEmitter<Template>();
  @Output() typeChange = new EventEmitter<string>();
  @Output() tenantChange = new EventEmitter<void>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<string>();

  templateForm: FormGroup;
  tenants: Tenant[];
  deviceCategories: DeviceCategory[];
  types: SelectItem[];

  deviceTypeSelected: boolean;

  constructor(private formBuilder: FormBuilder,
              private tenantsService: TenantsService,
              private authGuard: AuthGuard,
              private i18n: TranslocoService,
              private previousService: PreviousRouteService) {
  }

  ngOnInit(): void {
    this.loadAndCreateForm();
  }

  private loadAndCreateForm(): void {
    this.tenantsService.getTenantSummaries().toPromise().then(response => {
      this.tenants = response.tenants;
      if (!this.authGuard.superAdminRequired()) {
        this.template.tenant = this.tenants[0];
      }
      this.loadTypes();
      this.createForm(this.template);
    });
  }

  private createForm(template: Template): void {
    const tenant = template.tenant ? {name: template.tenant.name, uuid: template.tenant.uuid} : null;

    this.templateForm = this.formBuilder.group({
      name: [template.name, [Validators.required, Validators.maxLength(255), noSpecialCharacters]],
      description: [ template.description, [Validators.maxLength(255)] ],
      type: [template.type, [Validators.required, Validators.maxLength(255)]],
      tenant: [tenant, [Validators.required]],
      deviceCategory: [{value: template.deviceCategory, disabled: true}],
      uuid: [template.uuid]
    });
    this.templateForm.markAsPending();

    if (this.template.deviceCategory) {
      this.deviceTypeSelected = true;
      if (tenant) {
        this.loadCategories(this.template.tenant.uuid);
      }
    }

    if (this.mode === 'edit') {
      this.templateForm.get('tenant').disable();
      this.templateForm.get('type').disable();
      if (template.type === 'DEVICE') {
        this.deviceTypeSelected = true;
        this.loadCategories(this.template.tenant.uuid);
      }
    }
  }

  private loadCategories(tenantUuid: string): void {
    this.templateForm.get('deviceCategory').disable();
    this.tenantsService.getTenantDeviceCategories(tenantUuid).toPromise().then(response => {
      this.deviceCategories = response.deviceCategories
        .sort((a, b) => a.name > b.name ? 1 : -1);
      this.templateForm.get('deviceCategory').enable();
    });
  }

  private loadTypes(): void {
    this.deviceTypeSelected = false;
    this.i18n.selectTranslate('model.device').subscribe(() => {
      this.types = [
        {label: this.i18n.translate('model.device'), value: 'DEVICE'},
        {label: this.i18n.translate('model.asset'), value: 'ASSET'}
      ];
    });
  }

  onTenantChange(event): void {
    this.tenantChange.emit();
    if (event.value && this.templateForm.get('type').value === 'DEVICE') {
      this.templateForm.get('deviceCategory').setValue(null);
      this.loadCategories(event.value.uuid);
    }
  }

  onTypeChange(event): void {
    this.typeChange.emit(event.value);
    this.templateForm.get('deviceCategory').setValue(null);
    if (event.value === 'DEVICE') {
      this.deviceTypeSelected = true;
      if (this.templateForm.get('tenant').value) {
        this.loadCategories(this.templateForm.get('tenant').value.uuid);
      }
    } else if (event.value === 'ASSET') {
      this.deviceTypeSelected = false;
      this.templateForm.get('deviceCategory').disable();
    }
  }

  isValid(): boolean {
    if (this.templateForm?.get('type').value === 'DEVICE' && !this.templateForm?.get('deviceCategory').value) {
      return false;
    }
    return this.templateForm?.valid;
  }

  onNextStep(): void {
    if (this.templateForm.valid) {
      this.nextStep.emit();
      this.templateChange.emit(this.templateForm.getRawValue());
    }
  }

  onCancel(): void {
    this.cancel.emit(this.previousService.getPreviousUrl());
  }

}
